import { ChevronsUpDown, LogOut, MonitorSmartphone } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

import { useSession } from "@/core/auth/session";
import { ThemeMenuItems } from "@/core/theme/ThemeSwitch";
import { Avatar } from "@/ui/avatar";
import { cn } from "@/ui/cn";
import { ConfirmDialog } from "@/ui/confirm-dialog";

export const menuItemClass =
  "flex min-h-10 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium outline-none select-none data-[highlighted]:bg-surface-muted [&_svg]:size-[18px] [&_svg]:shrink-0";

/**
 * The account card at the bottom of the menu: it opens the person's settings (theme), any extra actions
 * (`children`, e.g. leaving a brand) and sign-out. Not modal, so a dialog opened from it gets the focus.
 */
export function AccountMenu({
  subtitle,
  compact = false,
  rail = false,
  children,
}: {
  subtitle?: string;
  /** A small avatar button for a page header (pages before a brand is chosen), not the sidebar card. */
  compact?: boolean;
  /** Just the avatar, for the collapsed sidebar; the menu opens beside it. */
  rail?: boolean;
  children?: ReactNode;
}) {
  const { me, signOut } = useSession();
  const [confirmEverywhere, setConfirmEverywhere] = useState(false);
  const [busy, setBusy] = useState(false);
  const onSignOut = async (everywhere = false) => {
    try {
      await signOut({ everywhere });
    } catch {
      toast.error(
        everywhere
          ? "Couldn't reach Brocoly to sign out everywhere. You're signed out on this computer; try again from any device."
          : "Couldn't reach Brocoly to sign out. You're signed out on this computer.",
      );
    }
  };
  return (
    <>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger asChild>
          {rail ? (
            <button
              type="button"
              aria-label="Account and settings"
              title={me?.name || me?.email || undefined}
              className="flex size-12 items-center justify-center rounded-xl transition-colors hover:bg-surface-muted data-[state=open]:bg-surface-muted"
            >
              <Avatar name={me?.name} email={me?.email} />
            </button>
          ) : compact ? (
            <button
              type="button"
              aria-label="Account and settings"
              className="flex min-w-0 items-center gap-2 rounded-xl p-1.5 transition-colors hover:bg-surface-muted data-[state=open]:bg-surface-muted"
            >
              <Avatar name={me?.name} email={me?.email} />
              <span className="hidden max-w-48 truncate text-sm font-semibold md:inline">
                {me?.name || me?.email}
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-muted" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              aria-label="Account and settings"
              className="flex w-full items-center gap-2.5 rounded-xl bg-surface-muted p-3 text-left transition-colors hover:bg-surface-sunken data-[state=open]:bg-surface-sunken lg:short:p-2"
            >
              <Avatar name={me?.name} email={me?.email} />
              <span className="flex min-w-0 grow flex-col">
                <span className="truncate text-sm font-semibold">{me?.name || me?.email}</span>
                {subtitle && <span className="truncate text-xs text-muted">{subtitle}</span>}
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-muted" aria-hidden="true" />
            </button>
          )}
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side={rail ? "right" : compact ? "bottom" : "top"}
            align={rail || compact ? "end" : "start"}
            sideOffset={8}
            className={cn(
              "z-50 min-w-60 rounded-xl border border-border bg-surface p-1.5 text-ink shadow-xl",
              !compact && !rail && "w-[var(--radix-dropdown-menu-trigger-width)]",
            )}
          >
            {me?.email && (
              <DropdownMenu.Label className="truncate px-2.5 pt-1.5 pb-2 text-xs text-muted">
                {me.email}
              </DropdownMenu.Label>
            )}
            <DropdownMenu.Label className="px-2.5 pt-1 pb-1 text-[11px] font-bold tracking-[0.08em] text-faint uppercase">
              Theme
            </DropdownMenu.Label>
            <ThemeMenuItems itemClassName={menuItemClass} />
            <DropdownMenu.Separator className="my-1.5 h-px bg-border-subtle" />
            {children}
            <DropdownMenu.Item onSelect={() => void onSignOut()} className={menuItemClass}>
              <LogOut aria-hidden="true" className="text-muted" />
              Sign out
            </DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => setConfirmEverywhere(true)} className={menuItemClass}>
              <MonitorSmartphone aria-hidden="true" className="text-muted" />
              Sign out everywhere
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {confirmEverywhere && (
        <ConfirmDialog
          title="Sign out everywhere"
          subject={me?.email ?? "Your account"}
          tone="danger"
          effect="Ends your Brocoly sign-in on every portal, browser and phone, including the Brocoly app. Use it if you lost a device or forgot to sign out on a shared computer."
          confirmLabel="Sign out everywhere"
          busy={busy}
          error={null}
          onConfirm={() => {
            setBusy(true);
            void onSignOut(true).finally(() => setBusy(false));
          }}
          onClose={() => setConfirmEverywhere(false)}
        />
      )}
    </>
  );
}

/** A menu item for a change that takes something away (e.g. leaving a brand): red, with its own confirm. */
export function DangerMenuItem({ onSelect, children }: { onSelect: () => void; children: ReactNode }) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(menuItemClass, "text-danger data-[highlighted]:bg-danger-soft")}
    >
      {children}
    </DropdownMenu.Item>
  );
}
